import { IMPORT_CSV_LIMIT } from 'app/main/subscribersV4/AddSubscibers/constants'
import { MAX_CSV_SIZE, HEADERS_IMPORT_CSV_FILE } from '../../constansts'
import moment from 'moment'

export const getParseCsvData = async (data) => {
  const parseData = []
  const newLinebrk = data.split(/\r\n|\n/)
  for (let i = 0; i < newLinebrk.length; i++) {
    if (newLinebrk[i].trim() !== '') {
      parseData.push(newLinebrk[i].split(','))
    }
  }
  return parseData
}

export const checkHeadersRequiredDependentOnEachOther = ({
  required,
  header,
  headerKeys,
}) => {
  const mutuallyExclusiveHeaders = {
    email: 'phone_number',
    phone_number: 'email',
  }

  const dependentHeadings = {
    country_code: 'phone_number',
  }

  const headerKeysSet = new Set(headerKeys)
  const exclusiveHeader = mutuallyExclusiveHeaders[header]
  const dependentHeader = dependentHeadings[header]

  if (dependentHeader) {
    return headerKeysSet.has(dependentHeader)
  }
  if (exclusiveHeader) {
    return !headerKeysSet.has(exclusiveHeader)
  }

  return required
}

export const getInfoMappedHeadersInCSVFile = (headerKeys) => {
  const headersMap = new Map(
    HEADERS_IMPORT_CSV_FILE.map(({ header, required }) => [
      header,
      { required },
    ]),
  )

  const infoMappedHeadersMap = new Map()
  let isInvalidHeaderKey = false

  headerKeys.forEach((headerKey) => {
    const foundHeader = headersMap.get(headerKey)
    if (foundHeader) {
      infoMappedHeadersMap.set(headerKey, {
        headerKey,
        required: foundHeader.required,
        isInvalid: false,
      })
    } else {
      isInvalidHeaderKey = true
    }
  })

  HEADERS_IMPORT_CSV_FILE.forEach(({ header, required }) => {
    if (!infoMappedHeadersMap.has(header)) {
      infoMappedHeadersMap.set(header, {
        headerKey: header,
        required: checkHeadersRequiredDependentOnEachOther({
          required,
          header,
          headerKeys,
        }),
        isInvalid: true,
      })
    }
  })

  const infoMappedHeaders = Array.from(infoMappedHeadersMap.values())
  return { infoMappedHeaders, isInvalidHeaderKey }
}

export const validationCSVFileHeaders = (headerKeys) => {
  const headersOfCreatedByIndex = []
  headerKeys = headerKeys.map((header, index) => {
    const trimmedHeader = header.trim()
    if (!trimmedHeader) {
      const newHeader = createHeaderByIndex(index)
      headersOfCreatedByIndex.push(newHeader)
      return newHeader
    }
    return trimmedHeader
  })
  const objWithIndex = {}

  const constantsKeys = HEADERS_IMPORT_CSV_FILE.map(({ header }) => header)

  const notDefinedLabel =
    'subscribers.import.previewTable.dragAndDrop.notDefined.text'

  const validatedHeaderKeys = headerKeys.map((item, index) => {
    const indexItem = constantsKeys.indexOf(item)
    let isInvalid = false
    let label = notDefinedLabel

    if (indexItem < 0) {
      isInvalid = true
    } else {
      const { label: keyLabel } = HEADERS_IMPORT_CSV_FILE[indexItem]
      label = keyLabel
    }
    objWithIndex[item] = index
    return {
      headerKey: item,
      changedHeaderkey: '',
      label,
      isInvalid,
    }
  })
  return { validatedHeaderKeys, objWithIndex, headersOfCreatedByIndex }
}

export const generateRowsData = (
  csvBody,
  objWithIndex,
  optionsLanguages,
  defaultLanguage,
  headersOfCreatedByIndex,
  validatedHeaderKeys,
) => {
  const objWithIndexKeys = Object.keys(objWithIndex)
  const languageCodesMap = new Map(
    optionsLanguages.map(({ code, name }) => [code, name]),
  )
  const getProcessedValue = (columnName, rowValue) => {
    const trimmedValue = rowValue?.trim() || ''
    switch (columnName) {
      case 'preferred_language':
        return trimmedValue
          ? languageCodesMap.get(trimmedValue) || defaultLanguage
          : defaultLanguage

      case 'date_of_birth':
      case 'passport_expiration_date':
        return trimmedValue ? formattedDate(trimmedValue, 'M/D/YYYY') : null
      case 'contact_permission':
        return trimmedValue || 'Yes'
      default:
        return trimmedValue
    }
  }

  const cleanCountryCode = (countryCode) => countryCode.replace(/\+/g, '')
  let rows = csvBody.map((csvRow, index) => {
    const newRow = { id: index }

    objWithIndexKeys.forEach((columnName) => {
      const csvValue = csvRow[objWithIndex[columnName]]
      const processedValue = getProcessedValue(columnName, csvValue)

      newRow[columnName] =
        columnName === 'country_code'
          ? cleanCountryCode(processedValue)
          : processedValue
    })

    return newRow
  })

  const { emptyColumns, rows: newRows } = removeEmptyColumns(
    rows,
    headersOfCreatedByIndex,
  )

  let filteredValidateHeaderKeys = validatedHeaderKeys

  if (emptyColumns.length) {
    filteredValidateHeaderKeys = validatedHeaderKeys.filter(
      (header) => !emptyColumns.includes(header.headerKey),
    )
  }

  return { rows: newRows, filteredValidateHeaderKeys }
}

export const validationMaxCSVSize = (size, formatMessage) => {
  if (MAX_CSV_SIZE < size) {
    throw formatMessage(
      { id: 'subscriber.importBox.validation.fileSize' },
      { maxSize: `${MAX_CSV_SIZE} MB` },
    )
  }
}

export const validationMaxCSVLimit = (csvBodyLength, formatMessage) => {
  if (csvBodyLength - 1 > IMPORT_CSV_LIMIT) {
    throw formatMessage(
      { id: 'import.csv.limit.error.message' },
      { limit: IMPORT_CSV_LIMIT },
    )
  }
}
export const hasValidCSVFile = (csvBody, formatMessage) => {
  const hasValidContent =
    Array.isArray(csvBody) &&
    csvBody.some(
      (row) =>
        Array.isArray(row) &&
        row.some((cell) => typeof cell === 'string' && cell.trim()),
    )

  if (!hasValidContent) {
    throw formatMessage({ id: 'import.csv.emptyFile.error.message' })
  }
}

export const isValidMissingRequiredFields = (headerKeys, formatMessage) => {
  const requiredFields = ['first_name', 'last_name']
  const contactFields = ['email', 'phone_number']

  headerKeys = headerKeys.map((headerkey) => headerkey.trim())

  const missingFields = requiredFields.filter(
    (field) => !headerKeys.includes(field),
  )
  const errorMessage = formatMessage({
    id: 'import.csv.missingRequiredFields.error.message',
  })
  if (missingFields.length > 0) {
    throw errorMessage
  }

  const hasContactField = contactFields.some((field) =>
    headerKeys.includes(field),
  )

  if (!hasContactField) {
    throw errorMessage
  }
}

export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const formattedDate = (date, format) => {
  const dateFormats = [
    'M/D/YYYY',
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'D/M/YYYY',
    'DD/MM/YYYY',
    'M-D-YYYY',
    'D-M-YYYY',
    'YYYY/MM/DD',
    'YYYY.MM.DD',
    'DD.MM.YYYY',
    'MM-DD-YYYY',
    'YYYY/MM/DD',
    'DD-MMM-YYYY',
    'MMM D, YYYY',
    'D MMM YYYY',
    'D MMMM YYYY',
    'MMMM D, YYYY',
    'YYYYMMDD',
    'MMDDYYYY',
  ]

  const parsedDate = moment(date, dateFormats)

  if (!parsedDate.isValid()) {
    return date
  }

  return parsedDate.format(format)
}

export const createHeaderByIndex = (index) => `not_defined_header-${index}`

const removeEmptyColumns = (data, columns) => {
  if (!columns.length) return { emptyColumns: [], rows: data }

  const emptyColumns = columns.filter((column) =>
    data.every((row) => row[column] === ''),
  )

  const rows = data.map((row) => {
    const newRow = { ...row }
    emptyColumns.forEach((column) => delete newRow[column])
    return newRow
  })

  return { emptyColumns, rows }
}
